import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonSize, ButtonType, ColorType, MODAL_TYPES, useGlobalModalContext } from '../_ui';

interface ConfirmActionOptions {
  /** Modal heading. */
  title: string;
  /** Body content; accepts a string or a node (e.g. a <Trans> with the entity name). */
  message: ReactNode;
  /** Label of the confirm button. */
  confirmLabel: string;
  /** Visual style of the confirm button. Defaults to PRIMARY. */
  confirmType?: ButtonType;
  /** Heading icon tint (e.g. NEGATIVE for destructive actions). */
  iconColor?: ColorType;
  /** Suffix for the cancel/confirm button test ids, e.g. "PausePromoCode". */
  dataTestId: string;
  /**
   * Runs when the user confirms. The AlertModal closes itself before this fires, so
   * just perform the mutation here (and any post-success navigation); the api toast
   * pipeline reports success/failure. Async handlers are awaited and their rejections
   * caught here, so `await mutation().unwrap()` followed by post-success work behaves
   * correctly — the post-success step is skipped when the mutation fails.
   */
  onConfirm: () => Promise<void> | void;
}

/**
 * Opens a cancel/confirm alert modal. Centralizes the boilerplate shared by the
 * promotion and promo-code pause/resume/archive/publish confirmation dialogs.
 */
export const useConfirmActionModal = () => {
  const { t } = useTranslation();
  const { showModal } = useGlobalModalContext();

  return useCallback(
    ({
      title,
      message,
      confirmLabel,
      confirmType = ButtonType.PRIMARY,
      iconColor,
      dataTestId,
      onConfirm,
    }: ConfirmActionOptions) => {
      showModal(MODAL_TYPES.ALERT_MODAL, {
        title,
        width: '400px',
        height: 'max-content',
        message,
        iconColor,
        buttons: [
          {
            label: t('cancel'),
            type: ButtonType.TERTIARY,
            size: ButtonSize.SMALL,
            dataTestId: `cancel${dataTestId}`,
          },
          {
            label: confirmLabel,
            type: confirmType,
            size: ButtonSize.SMALL,
            dataTestId: `confirm${dataTestId}`,
            onClick: async () => {
              try {
                await onConfirm();
              } catch {
                // Failures are surfaced by the base.api toast pipeline.
              }
            },
          },
        ],
      });
    },
    [showModal, t],
  );
};
